import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

interface FormField {
  id: number;
  field_name: string;
  field_label: string;
  field_type: string;
  options_list?: string;
  is_required: number;
  form_type: 'VISITOR_REGISTRATION' | 'EXPRESS_INTEREST';
}

const Sticky: React.FC = () => {
  const [isExpressModalOpen, setExpressModalOpen] = useState(false);
  const [isVisitorModalOpen, setVisitorModalOpen] = useState(false);

  const [expressFields, setExpressFields] = useState<FormField[]>([]);
  const [visitorFields, setVisitorFields] = useState<FormField[]>([]);

  const [expressFormData, setExpressFormData] = useState<Record<string, any>>({});
  const [visitorFormData, setVisitorFormData] = useState<Record<string, any>>({});

  const [submittingExpress, setSubmittingExpress] = useState(false);
  const [submittingVisitor, setSubmittingVisitor] = useState(false);

  const expressModalRef = useRef<HTMLDivElement>(null);
  const visitorModalRef = useRef<HTMLDivElement>(null);

  // Default fallback fields if DB is empty
  const defaultExpressFields: FormField[] = [
    { id: 1, field_name: 'full_name', field_label: 'Full Name', field_type: 'text', is_required: 1, form_type: 'EXPRESS_INTEREST' },
    { id: 2, field_name: 'email', field_label: 'Email Address', field_type: 'email', is_required: 1, form_type: 'EXPRESS_INTEREST' },
    { id: 3, field_name: 'mobile', field_label: 'Mobile / WhatsApp Number', field_type: 'phone', is_required: 1, form_type: 'EXPRESS_INTEREST' },
    { id: 4, field_name: 'company_name', field_label: 'Company / Business Name', field_type: 'text', is_required: 0, form_type: 'EXPRESS_INTEREST' },
    { id: 5, field_name: 'business_category', field_label: 'Business Category / Industry', field_type: 'text', is_required: 0, form_type: 'EXPRESS_INTEREST' },
    { id: 6, field_name: 'interest_reason', field_label: 'Why do you want to join RMB Jaipur?', field_type: 'textarea', is_required: 0, form_type: 'EXPRESS_INTEREST' }
  ];

  const defaultVisitorFields: FormField[] = [
    { id: 7, field_name: 'visitor_name', field_label: 'Visitor Full Name', field_type: 'text', is_required: 1, form_type: 'VISITOR_REGISTRATION' },
    { id: 8, field_name: 'visitor_email', field_label: 'Email Address', field_type: 'email', is_required: 1, form_type: 'VISITOR_REGISTRATION' },
    { id: 9, field_name: 'visitor_phone', field_label: 'Mobile / WhatsApp Number', field_type: 'phone', is_required: 1, form_type: 'VISITOR_REGISTRATION' },
    { id: 10, field_name: 'visitor_company', field_label: 'Company / Firm Name', field_type: 'text', is_required: 0, form_type: 'VISITOR_REGISTRATION' },
    { id: 11, field_name: 'invited_by_member', field_label: 'Invited By (RMB Member Name)', field_type: 'text', is_required: 0, form_type: 'VISITOR_REGISTRATION' },
    { id: 12, field_name: 'meeting_date', field_label: 'Preferred Meeting Date', field_type: 'date', is_required: 0, form_type: 'VISITOR_REGISTRATION' }
  ];

  useEffect(() => {
    // Fetch dynamic form fields from Admin Form Builder API
    axios.get('https://rmbbackend.rmbjaipur.com/auth/get-in-touch/fields')
      .then(res => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          const allFields: FormField[] = res.data.data;
          const exp = allFields.filter(f => f.form_type === 'EXPRESS_INTEREST');
          const vis = allFields.filter(f => f.form_type === 'VISITOR_REGISTRATION');

          setExpressFields(exp.length > 0 ? exp : defaultExpressFields);
          setVisitorFields(vis.length > 0 ? vis : defaultVisitorFields);
        } else {
          setExpressFields(defaultExpressFields);
          setVisitorFields(defaultVisitorFields);
        }
      })
      .catch(() => {
        setExpressFields(defaultExpressFields);
        setVisitorFields(defaultVisitorFields);
      });
  }, []);

  // Close modals when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isExpressModalOpen && expressModalRef.current && !expressModalRef.current.contains(e.target as Node)) {
        setExpressModalOpen(false);
      }
      if (isVisitorModalOpen && visitorModalRef.current && !visitorModalRef.current.contains(e.target as Node)) {
        setVisitorModalOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpressModalOpen, isVisitorModalOpen]);

  const handleExpressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingExpress(true);
    try {
      await axios.post('https://rmbbackend.rmbjaipur.com/auth/get-in-touch/submit', {
        form_type: 'EXPRESS_INTEREST',
        data: expressFormData
      });
      alert('🎉 Thank you for expressing your interest in RMB Jaipur Fellowship! We will get in touch with you shortly.');
      setExpressFormData({});
      setExpressModalOpen(false);
    } catch (err) {
      console.error('Error submitting express interest:', err);
      alert('Failed to submit express interest. Please try again.');
    } finally {
      setSubmittingExpress(false);
    }
  };

  const handleVisitorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingVisitor(true);
    try {
      await axios.post('https://rmbbackend.rmbjaipur.com/auth/get-in-touch/submit', {
        form_type: 'VISITOR_REGISTRATION',
        data: visitorFormData
      });
      alert('🎟️ Thank you! Your visitor registration request has been submitted successfully.');
      setVisitorFormData({});
      setVisitorModalOpen(false);
    } catch (err) {
      console.error('Error submitting visitor registration:', err);
      alert('Failed to submit visitor registration. Please try again.');
    } finally {
      setSubmittingVisitor(false);
    }
  };

  const renderDynamicField = (field: FormField, formData: Record<string, any>, setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>) => {
    const handleChange = (val: any) => {
      setFormData(prev => ({ ...prev, [field.field_name]: val }));
    };

    if (field.field_type === 'textarea') {
      return (
        <div key={field.id} className="space-y-1 my-2">
          <label className="text-xs font-bold text-slate-700">
            {field.field_label} {field.is_required === 1 && <span className="text-red-500">*</span>}
          </label>
          <textarea
            required={field.is_required === 1}
            value={formData[field.field_name] || ''}
            onChange={(e) => handleChange(e.target.value)}
            rows={3}
            placeholder={`Enter ${field.field_label}`}
            className="w-full border border-slate-300 p-2.5 rounded-lg text-xs outline-none focus:border-[#02256D] font-medium"
          />
        </div>
      );
    }

    if (field.field_type === 'select') {
      const options = field.options_list ? field.options_list.split(',').map(o => o.trim()) : [];
      return (
        <div key={field.id} className="space-y-1 my-2">
          <label className="text-xs font-bold text-slate-700">
            {field.field_label} {field.is_required === 1 && <span className="text-red-500">*</span>}
          </label>
          <select
            required={field.is_required === 1}
            value={formData[field.field_name] || ''}
            onChange={(e) => handleChange(e.target.value)}
            className="w-full border border-slate-300 p-2.5 rounded-lg text-xs outline-none focus:border-[#02256D] font-medium"
          >
            <option value="">-- Select {field.field_label} --</option>
            {options.map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div key={field.id} className="space-y-1 my-2">
        <label className="text-xs font-bold text-slate-700">
          {field.field_label} {field.is_required === 1 && <span className="text-red-500">*</span>}
        </label>
        <input
          type={field.field_type === 'phone' ? 'tel' : field.field_type}
          required={field.is_required === 1}
          value={formData[field.field_name] || ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={`Enter ${field.field_label}`}
          className="w-full border border-slate-300 p-2.5 rounded-lg text-xs outline-none focus:border-[#02256D] font-medium"
        />
      </div>
    );
  };

  return (
    <>
      {/* Floating Animated Side CTAs (Highlighted & Visible on all devices) */}
      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-3.5 items-end p-2 pointer-events-auto">

        {/* 1. Express Interest CTA Button */}
        <button
          type="button"
          onClick={() => setExpressModalOpen(!isExpressModalOpen)}
          className="group relative flex items-center gap-2.5 bg-gradient-to-r from-[#02256D] to-[#0A1838] text-[#FDBC12] border-2 border-[#FDBC12] px-4 py-2.5 rounded-l-2xl shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer overflow-hidden animate-pulse-glow"
        >
          {/* Animated Pulsing Beacon Dot */}
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FDBC12] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#FDBC12]"></span>
          </span>

          <span className="text-xs sm:text-sm font-black tracking-wide flex items-center gap-1.5 whitespace-nowrap">
            🤝 Express Interest
          </span>

          {/* Shimmer Light Reflection Animation */}
          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
        </button>

        {/* 2. Visitor Registration CTA Button */}
        <button
          type="button"
          onClick={() => setVisitorModalOpen(!isVisitorModalOpen)}
          className="group relative flex items-center gap-2.5 bg-gradient-to-r from-[#E11D48] to-[#9F1239] text-white border-2 border-rose-300 px-4 py-2.5 rounded-l-2xl shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer overflow-hidden animate-bounce-subtle"
        >
          {/* Animated Pulsing Beacon Dot */}
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
          </span>

          <span className="text-xs sm:text-sm font-black tracking-wide flex items-center gap-1.5 whitespace-nowrap">
            🎟️ Visitor Registration
          </span>

          {/* Shimmer Light Reflection Animation */}
          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
        </button>
      </div>

      {/* Dynamic Express Interest Modal */}
      {isExpressModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-3xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200" ref={expressModalRef}>
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h2 className="text-lg font-black text-[#02256D]">🤝 Express Interest Form (RMB Fellowship)</h2>
              <button onClick={() => setExpressModalOpen(false)} className="text-slate-400 hover:text-red-500 font-bold text-sm">✕</button>
            </div>

            <form onSubmit={handleExpressSubmit}>
              {expressFields.map(f => renderDynamicField(f, expressFormData, setExpressFormData))}

              <div className="flex justify-end gap-3 pt-4 border-t mt-5">
                <button
                  type="button"
                  onClick={() => setExpressModalOpen(false)}
                  className="px-4 py-2 border rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingExpress}
                  className="px-6 py-2.5 bg-[#02256D] hover:bg-blue-900 text-[#FDBC12] font-black text-xs rounded-xl shadow-lg transition cursor-pointer"
                >
                  {submittingExpress ? 'Submitting...' : 'Submit Interest'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dynamic Visitor Registration Modal */}
      {isVisitorModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-3xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200" ref={visitorModalRef}>
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h2 className="text-lg font-black text-[#E11D48]">🎟️ Visitor Meeting Registration Form</h2>
              <button onClick={() => setVisitorModalOpen(false)} className="text-slate-400 hover:text-red-500 font-bold text-sm">✕</button>
            </div>

            <form onSubmit={handleVisitorSubmit}>
              {visitorFields.map(f => renderDynamicField(f, visitorFormData, setVisitorFormData))}

              <div className="flex justify-end gap-3 pt-4 border-t mt-5">
                <button
                  type="button"
                  onClick={() => setVisitorModalOpen(false)}
                  className="px-4 py-2 border rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingVisitor}
                  className="px-6 py-2.5 bg-[#E11D48] hover:bg-rose-700 text-white font-black text-xs rounded-xl shadow-lg transition cursor-pointer"
                >
                  {submittingVisitor ? 'Submitting...' : 'Confirm Registration'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Style block for custom pulse & shimmer keyframes */}
      <style>{`
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 15px rgba(253, 188, 18, 0.4); }
          50% { box-shadow: 0 0 28px rgba(253, 188, 18, 0.85); }
        }
        @keyframes bounceSubtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .animate-pulse-glow {
          animation: pulseGlow 2.5s infinite ease-in-out;
        }
        .animate-bounce-subtle {
          animation: bounceSubtle 2s infinite ease-in-out;
        }
      `}</style>
    </>
  );
};

export default Sticky;
